import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLookups } from "@/context/LookupContext";
import { toast } from "sonner";
import { formatINR } from "@/lib/currency";
import { formatDate } from "@/lib/datetime";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Edit3, ReceiptText } from "lucide-react";
import { norm } from "@/lib/normalize";
import { useConfirm } from "@/components/ConfirmDialog";

const empty = { category: "", description: "", amount: 0, expenseDate: "", paymentMethod: "" };

export default function Expenses() {
  const { user } = useAuth();
  const { lookups } = useLookups();
  const expenseCategories = (lookups?.expenseCategories || []).map((c) => c.name || c);
  const paymentMethods = (lookups?.paymentMethods || []).map((m) => m.name || m);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [filterMonth, setFilterMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [filterCategory, setFilterCategory] = useState("ALL");
  const isAdmin = norm(user.roleName) === "admin";

  const load = async () => {
    try { setRows((await api.get("/expenses")).data); } catch { toast.error("Failed"); }
  };
  useEffect(() => { load(); }, []);

  const filteredRows = rows.filter((e) => {
    if (filterCategory && filterCategory !== "ALL" && e.category !== filterCategory) return false;
    if (!filterMonth) return true;
    if (!e.expenseDate) return false;
    const d = new Date(e.expenseDate);
    const monthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    return monthStr === filterMonth;
  });

  const total = filteredRows.reduce((sum, e) => sum + Number(e.amount || 0), 0);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (editId) await api.put(`/expenses/${editId}`, payload);
      else await api.post("/expenses", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };

  const confirm = useConfirm();
  const del = async (id) => {
    if (!(await confirm("Delete this expense?"))) return;
    try { await api.delete(`/expenses/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="expenses-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Finance</div>
          <h1 className="font-heading text-4xl text-white inline-flex items-center gap-3"><ReceiptText className="w-8 h-8 text-rose-500" /> Expenses <span className="text-gold">({formatINR(total)})</span></h1>
        </div>
        <div className="flex items-center gap-3">
          <input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}
            className="h-10 px-3 rounded-md bg-emerald-elev border border-gold-subtle text-white text-sm focus:outline-none"
            data-testid="expense-month-filter" />
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="bg-emerald-elev border-gold-subtle text-white h-10 w-44">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Types</SelectItem>
              {expenseCategories.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button onClick={() => { setForm(empty); setEditId(null); }} data-testid="add-expense-btn" className="gold-button px-5 py-2.5 rounded-full inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New Expense</button>
            </DialogTrigger>
            <DialogContent className="bg-emerald-surface border-gold-subtle text-white max-w-lg">
              <DialogHeader><DialogTitle className="font-heading text-2xl">{editId ? "Edit" : "New"} Expense</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={(val) => setForm({ ...form, category: val })}>
                      <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-9">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {expenseCategories.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Payment Method</Label>
                    <Select value={form.paymentMethod} onValueChange={(val) => setForm({ ...form, paymentMethod: val })}>
                      <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-9">
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentMethods.map((m) => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Amount (₹)</Label><Input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                  <div><Label>Expense Date</Label><Input required type="date" value={form.expenseDate} onChange={(e) => setForm({ ...form, expenseDate: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1 text-white" /></div>
                </div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" placeholder="Optional details" /></div>
                <button type="submit" className="gold-button w-full h-11 rounded-md">Save</button>
              </form>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      <div className="bg-emerald-surface border border-gold-subtle rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="thead-row hover:bg-[rgba(197,160,89,0.06)]">
              <TableHead className="th-gold">Category</TableHead>
              <TableHead className="th-gold">Date</TableHead>
              <TableHead className="th-gold">Payment Method</TableHead>
              <TableHead className="th-gold text-right">Amount</TableHead>
              {isAdmin && <TableHead className="th-gold w-24"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRows.map((e) => (
              <TableRow key={e.id} className="border-gold-subtle" data-testid={`expense-row-${e.id}`}>
                <TableCell className="text-white">{e.category}</TableCell>
                <TableCell className="text-emerald-muted">{formatDate(e.expenseDate)}</TableCell>
                <TableCell className="text-emerald-muted">{e.paymentMethod}</TableCell>
                <TableCell className="text-right text-gold">{formatINR(e.amount)}</TableCell>
                {isAdmin && (
                  <TableCell className="text-right">
                    <button onClick={() => { setForm({ category: e.category || "", description: e.description || "", amount: e.amount, expenseDate: e.expenseDate || "", paymentMethod: e.paymentMethod || "" }); setEditId(e.id); setOpen(true); }} className="text-emerald-muted hover:text-gold p-1"><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => del(e.id)} className="text-emerald-muted hover:text-red-300 p-1 ml-2" data-testid={`delete-expense-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
